import { ScrollView } from "react-native";
import { View } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import type { CameraApiResponse } from "@src/features/cameras";
import { useCameraDetailsViewModel } from "@src/features/cameras";
import { sizes } from "@src/constants/sizes";
import { CameraSnapshotWeb } from "./CameraSnapshotWeb";
import { ArmStatusCard } from "./ArmStatusCard.web";
import { ScheduleMenuCard } from "./ScheduleMenuCard";
import { EditableCameraName } from "./EditableCameraName.web";
import { useTimelapseButtonAction, useTimelapseEnabled } from "@src/features/timelapse";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useTenantId } from "@src/features/tenants";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";
import { useSnapshots } from "@visioni/a3s-media/app";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";

interface CameraDetailsContentProps {
  camera: CameraApiResponse;
  alarmDetail?: AlarmConfiguration | null;
  siteId: string;
  onArmDisarm: () => void;
  onAdjust?: () => void;
  isArmDisarmLoading?: boolean;
}

export function CameraDetailsContent({
  camera,
  alarmDetail,
  siteId,
  onArmDisarm,
  onAdjust,
  isArmDisarmLoading = false,
}: CameraDetailsContentProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const { track } = useAmplitude();
  const tenantId = useTenantId();
  const { data: snapshots } = useSnapshots({});
  const { getBooleanFlag } = useFeatureFlags();
  const isUsersAndPermissionsOptionEnabled = getBooleanFlag(FEATURE_FLAGS.USERS_PERMISSIONS_OPTION);
  const {
    cameraKey,
    snapshotUrl,
    snapshotCapturedAt,
    isOnline,
    isArmed,
    statusText,
    scheduleTime,
    alertsCount,
    scheduleStatus,
    timezone,
  } = useCameraDetailsViewModel({ camera, alarmDetail, siteId });
  const timelapseEnabled = useTimelapseEnabled();
  const timelapseAction = useTimelapseButtonAction({
    siteId,
    cameraKey,
    cameraTimezone: timezone,
    enabled: timelapseEnabled,
  });
  const timelapseVisible = timelapseEnabled && timelapseAction.cameraEligible;
  const timelapseSubtitle = timelapseAction.subtitle;
  const onTimelapseActionPress = timelapseAction.onPress;
  const isTimelapseDisabled = timelapseAction.isBusy || !timelapseAction.isActionable;

  // Native splits the composite camera key on "@" to separate the asset id (left) from
  // the camera id (right). The /contacts/manage-access screen expects both halves so it
  // can render a "<asset> · <id>" header that matches the native screen exactly.
  const { cameraAssetPart, cameraIdPart } = useMemo(() => {
    if (!cameraKey) return { cameraAssetPart: "Camera", cameraIdPart: "" };
    const parts = cameraKey.split("@");
    return {
      cameraAssetPart: parts[0] ?? cameraKey,
      cameraIdPart: parts[1] ?? "",
    };
  }, [cameraKey]);

  // Navigation handlers
  const handlePlayPress = useCallback(() => {
    track(ANALYTICS_EVENTS.CAMERA_VIEW_LIVESTREAM_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/livestream`);
  }, [router, tenantId, siteId, cameraKey, track]);

  const handleAlarmSettingsPress = useCallback(() => {
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarm`);
  }, [router, tenantId, siteId, cameraKey]);

  const handleRecordingsPress = useCallback(() => {
    track(ANALYTICS_EVENTS.CAMERA_VIEW_CLIPS_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
    router.push(
      `/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraKey)}/clips`,
    );
  }, [router, cameraKey, tenantId, siteId, track]);

  const handleTimelapsePress = useCallback(() => {
    track(ANALYTICS_EVENTS.CAMERA_VIEW_TIMELAPSE_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
    void onTimelapseActionPress();
  }, [cameraKey, tenantId, siteId, track, onTimelapseActionPress]);

  const handleManageAccessPress = useCallback(() => {
    track(ANALYTICS_EVENTS.CAMERA_VIEW_MANAGE_ACCESS_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
    router.push({
      pathname: "/contacts/manage-access",
      params: {
        cameraNickname: camera.nickname ?? "",
        cameraAssetPart,
        cameraIdPart,
        siteId,
        cameraKey,
      },
    });
  }, [router, track, cameraKey, tenantId, siteId, camera.nickname, cameraAssetPart, cameraIdPart]);

  // Timelapse is hidden unless both checks pass: app user entitlement and
  // server-confirmed camera eligibility.
  // Skipping the prop entirely — rather than passing `undefined`
  // — keeps the row out of the React tree so analytics, ref forwarding
  // and side effects in `ScheduleMenuCard` don't run for hidden state.
  const timelapseHandler = timelapseVisible ? handleTimelapsePress : undefined;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: theme.spacing["6xl"],
        paddingBottom: theme.spacing["6xl"],
        alignItems: "center",
      }}
      showsVerticalScrollIndicator={false}
    >
      {/* Content container with max width */}
      <View width="100%" maxWidth={sizes.webContentMaxWidth}>
        {/* Title with inline rename */}
        <EditableCameraName cameraKey={cameraKey} siteId={siteId} currentNickname={camera.nickname ?? null} />

        {/* Camera Snapshot */}
        <View mb="6xl">
          <CameraSnapshotWeb
            snapshotUrl={snapshotUrl}
            snapshotCapturedAt={snapshotCapturedAt}
            onPlayPress={handlePlayPress}
            isOnline={isOnline}
            signedSnapshot={snapshots?.[cameraKey]}
          />
        </View>

        {/* Cards Row */}
        <View flexDirection="row" gap="3xl">
          {/* Arm Status Card */}
          <ArmStatusCard
            isArmed={isArmed}
            statusText={statusText}
            scheduleText={scheduleTime}
            onToggle={onArmDisarm}
            onAdjust={onAdjust}
            isLoading={isArmDisarmLoading}
          />

          {/* Schedule Menu Card */}
          <ScheduleMenuCard
            scheduleStatus={scheduleStatus}
            alertsCount={alertsCount}
            siteId={siteId}
            cameraKey={cameraKey}
            timezone={timezone}
            onAlarmSettingsPress={handleAlarmSettingsPress}
            onRecordingsPress={handleRecordingsPress}
            onTimelapsePress={timelapseHandler}
            timelapseSubtitle={timelapseSubtitle}
            timelapseDisabled={isTimelapseDisabled}
            alarmSettingsHref={
              `/tenants/${tenantId}/sites/${siteId}/cameras/${encodeURIComponent(cameraKey)}/alarm` as never
            }
            recordingsHref={
              `/tenants/${tenantId}/sites/${siteId}/cameras/${encodeURIComponent(cameraKey)}/clips` as never
            }
            onManageAccessPress={handleManageAccessPress}
            isManageAccessEnabled={isUsersAndPermissionsOptionEnabled}
          />
        </View>
      </View>
    </ScrollView>
  );
}
